
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BlogPost } from "@/types/blog";
import { ImportForm } from "@/components/wordpress/ImportForm";
import { ImportResults } from "@/components/wordpress/ImportResults";
import { PurgeImportControl } from "@/components/wordpress/PurgeImportControl";
import { JsonFileUploader } from "@/components/wordpress/JsonFileUploader"; 
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon, HelpCircle, FileJson } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const WordPressImport = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [results, setResults] = useState<BlogPost[]>([]);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      console.log("WordPressImport: No admin token found, redirecting to login");
      const returnPath = encodeURIComponent(location.pathname);
      navigate(`/admin/login?returnTo=${returnPath}`);
    } else {
      console.log("WordPressImport: Admin token found, user is authenticated");
      setAuthenticated(true);
    }
  }, [navigate, location.pathname]);

  const handleImportComplete = (posts: BlogPost[]) => {
    setResults(posts);
    setIsLoading(false);
  };

  const handleImportStart = () => {
    setIsImporting(true);
  };

  const handleImportEnd = () => {
    setIsImporting(false);
  };
  
  const handlePurgeComplete = () => {
    // Clear results if there were any
    setResults([]);
  };

  if (!authenticated) {
    return (
      <div className="container mx-auto py-10">
        <Helmet>
          <title>WordPress Import | edgefi Admin</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <Card>
          <CardHeader>
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>
              You need to be logged in as an administrator to import WordPress posts.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-center mb-4">Please log in with an administrator account to continue.</p>
            <Button 
              onClick={() => navigate(`/admin/login?returnTo=${encodeURIComponent(location.pathname)}`)} 
              className="bg-edgefi-purple hover:bg-purple-800"
            >
              Go to Login Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
      <Helmet>
        <title>WordPress Import | edgefi Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <Alert className="mb-6 bg-blue-50 border-blue-200">
        <InfoIcon className="h-4 w-4 text-blue-500" />
        <AlertTitle>WordPress Import Tool</AlertTitle>
        <AlertDescription>
          You can import content either directly from a WordPress site using its API 
          or by uploading a WordPress JSON export file.
        </AlertDescription>
      </Alert>

      {authenticated && (
        <PurgeImportControl 
          onPurgeComplete={handlePurgeComplete} 
          disabled={isLoading || isImporting}
        />
      )}
      
      <Tabs defaultValue="api" className="mt-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="api">WordPress API</TabsTrigger>
          <TabsTrigger value="json">JSON Upload</TabsTrigger>
        </TabsList>
        
        <TabsContent value="api" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Import from WordPress API</CardTitle>
              <CardDescription>
                Enter the URL of a WordPress site to import its posts via the REST API.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-6">
                <HelpCircle className="h-4 w-4" />
                <AlertTitle>Troubleshooting Tips</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Make sure the WordPress site has the REST API enabled</li>
                    <li>Always use https:// in the URL (http:// will be automatically converted)</li>
                    <li>If the site requires login, you may need to use the JSON upload feature instead</li>
                    <li>Try using the main domain without subdirectories</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <ImportForm 
                onImportComplete={handleImportComplete} 
                isLoading={isLoading} 
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="json" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Import from JSON File</CardTitle>
              <CardDescription>
                Upload a WordPress JSON export file to import posts.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-6">
                <FileJson className="h-4 w-4" />
                <AlertTitle>JSON Format</AlertTitle>
                <AlertDescription>
                  <p className="mb-2">Upload a JSON file exported from WordPress or containing WordPress posts in a valid format.</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>The file should contain an array of posts or a single post object</li>
                    <li>Each post should have at minimum: title, content, and date fields</li>
                    <li>To export from WordPress, use the built-in export tool or a plugin like "Export All With JSON"</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <JsonFileUploader onImportComplete={handleImportComplete} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <ImportResults 
        results={results} 
        authenticated={authenticated}
        onImportStart={handleImportStart}
        onImportComplete={handleImportEnd}
      />
    </div>
  );
};

export default WordPressImport;
